import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CalendarCheck, Bell, CheckCircle2, Clock, Users, Mail, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-700",
  pending: "bg-amber-100 text-amber-700",
  confirmed: "bg-green-100 text-green-700",
  completed: "bg-slate-100 text-slate-600",
  cancelled: "bg-red-100 text-red-700",
};

export default function Dashboard() {
  const { data: stats } = useQuery({ queryKey: ["admin-stats"], queryFn: () => api.get("/admin/stats").then((r) => r.data) });

  const cards = [
    { label: "Total Enquiries", value: stats?.total_enquiries, icon: CalendarCheck, color: "text-brand bg-brand-light" },
    { label: "New Enquiries", value: stats?.new_enquiries, icon: Bell, color: "text-blue-600 bg-blue-50" },
    { label: "Confirmed", value: stats?.confirmed_bookings, icon: CheckCircle2, color: "text-green-600 bg-green-50" },
    { label: "Pending", value: stats?.pending_bookings, icon: Clock, color: "text-amber-600 bg-amber-50" },
    { label: "Customers", value: stats?.total_customers, icon: Users, color: "text-purple-600 bg-purple-50" },
    { label: "Messages", value: stats?.total_messages, icon: Mail, color: "text-rose-600 bg-rose-50" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-2xl font-heading font-bold text-navy-heading mb-1">Dashboard</h1>
      <p className="text-slate-500 text-sm mb-6">Overview of your bookings and enquiries.</p>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${c.color}`}><c.icon className="w-5 h-5" /></div>
            <div className="text-2xl font-heading font-extrabold text-navy-heading">{c.value ?? "—"}</div>
            <div className="text-xs text-slate-500 mt-0.5">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="font-heading font-bold text-navy-heading">Recent Enquiries</h2>
          <Link to="/admin/bookings" className="text-sm text-brand font-semibold inline-flex items-center gap-1">View all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr><th className="px-5 py-3 font-medium">Name</th><th className="px-5 py-3 font-medium">Phone</th><th className="px-5 py-3 font-medium">Trip</th><th className="px-5 py-3 font-medium">Route</th><th className="px-5 py-3 font-medium">Status</th></tr>
            </thead>
            <tbody>
              {(stats?.recent || []).map((b) => (
                <tr key={b.id} className="border-t border-slate-100">
                  <td className="px-5 py-3 font-medium text-navy-heading">{b.full_name}</td>
                  <td className="px-5 py-3 text-slate-600">{b.phone}</td>
                  <td className="px-5 py-3 text-slate-600">{b.trip_type || "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{b.pickup_location || "—"} → {b.drop_location || "—"}</td>
                  <td className="px-5 py-3"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[b.status] || "bg-slate-100"}`}>{b.status}</span></td>
                </tr>
              ))}
              {(!stats?.recent || stats.recent.length === 0) && (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400">No enquiries yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
